import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import DatePicker from "react-datepicker";
import moment from "moment";
import FetchData, { CreateEntry, GetList, GetOne } from "../../providers/dataProvider";
import { CurrencyConvertion, FormatValueDecimal } from "../../utils/Validate";
import SaveSuccessModel from "../../components/common/SaveSuccessModel";
import ErrorMessage from "../../components/common/ErrorMessage";
import "../../pages/quotation/Forecast.scss";

const ExampleCustomInput = forwardRef(({ value, onClick, onChange }, ref) => (
  <input
    value={value}
    className="example-custom-input form-control"
    onClick={onClick}
    onChange={onChange}
    ref={ref}
    readOnly
  />
));

const toInputDate = (date) => {
  if (!date) return "";
  const dt = new Date(date);
  if (Number.isNaN(dt.getTime())) return "";

  const year = dt.getFullYear();
  const month = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const toApiDateString = (date) => {
  if (!date) return "";
  const dt = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(dt.getTime())) return "";

  const year = dt.getFullYear();
  const month = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}T00:00:00`;
};

const getForecastLineKey = (line, index = "") =>
  String(
    line?.forecastProductId ||
      line?.rowForecast?.forecastProductId ||
      line?.rowForecast?.id ||
      `${line?.forecastRecordId || line?.id || "forecast"}-${line?.quoteProductId || index}`
  );

const formatHistoryDate = (dateValue, withTime = false) => {
  if (!dateValue) return "-";

  const date = moment.utc(dateValue).local();

  if (!date.isValid()) return "-";

  return withTime
    ? date.format("DD-MMM-YYYY hh:mm A")
    : date.format("DD-MMM-YYYY");
};

const buildHistoryTimelineRows = (histories = []) => {
    const sortedHistories = [...histories].sort(
        (a, b) =>
            new Date(a?.revisedOn || a?.createdOn || 0) -
            new Date(b?.revisedOn || b?.createdOn || 0)
    );

    return sortedHistories.map((historyItem, index) => {

        const revisionNo =
            historyItem?.revisionNo !== undefined &&
            historyItem?.revisionNo !== null
                ? Number(historyItem.revisionNo)
                : index;

        const isInitialRevision = revisionNo === 0;

        // Original forecast date
        const forecastDate =
            index === 0
                ? historyItem?.forecastedDate ||
                  historyItem?.revisedDate ||
                  historyItem?.expectedDate
                : sortedHistories[0]?.forecastedDate ||
                  sortedHistories[0]?.revisedDate ||
                  sortedHistories[0]?.expectedDate;

        // For Rev 01 onwards, revised date is the date
        // stored in this history record.
        const revisedDate = isInitialRevision
            ? ""
            : historyItem?.revisedDate ||
              historyItem?.forecastedDate ||
              historyItem?.expectedDate;

        // Rev 00 = creation only
        // Rev 01+ = revision date/time
        const revisedOn = isInitialRevision
            ? ""
            : historyItem?.revisedOn ||
              historyItem?.createdOn;

        return {
            ...historyItem,

            revisionNo,

            revisionLabel:
                `Rev ${String(revisionNo).padStart(2, "0")}`,

            forecastedDate: forecastDate || "-",

            revisedDate: revisedDate || "-",

            revisedOn: revisedOn || "-",

            createdBy: historyItem?.createdBy || ""
        };
    });
};

const ForecastViewModal = ({
  show,
  onHide,
  currentForecast,
  currentQuotation,
  forecastData,
  currencyDetail,
  setQuotationData,
  forecastedItemIds
}) => {
  const { data: userData } = FetchData("userprofile");
  const [openHistory, setOpenHistory] = useState({ show: false, currentItem: null });
  const [editingLine, setEditingLine] = useState(null);
  const [editValues, setEditValues] = useState({
    expectedDate: "",
  });
  const [forecastList, setForecastList] = useState([]);
  const [changeForecast, setChangeForecast] = useState(false);
  const [createMsg, setCreateMsg] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorShow, setErrorShow] = useState({
    show: false,
    errorMessage: "",
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [forecastApiData, setForecastApiData] = useState([]);
  const [selectedLineKeys, setSelectedLineKeys] = useState([]);
  const [bulkDate, setBulkDate] = useState("");
  const selectAllLinesRef = useRef(null);
  const mobileSelectAllLinesRef = useRef(null);
  const currentUserIdentityId = localStorage.getItem("authidentity");

  const loadForecastApiData = () => {
    if (!show || !forecastedItemIds?.size) {
      setForecastApiData([]);
      return;
    }

    const itemIds = Array.from(forecastedItemIds);
    const responses = [];
    let completed = 0;

    itemIds.forEach((quoteProductId) => {
      GetOne("forecast", { id: quoteProductId }, (response) => {
        completed += 1;

        if (response) {
          responses.push(response);
        }

        if (completed === itemIds.length) {
          setForecastApiData(responses);
        }
      });
    });
  };

  useEffect(() => {
    loadForecastApiData();
  }, [show, forecastedItemIds]);


const forecastedQuoteProductIds = useMemo(
  () => Array.from(forecastedItemIds || []).map(String),
  [forecastedItemIds]
);

  const forecastApiRecords = useMemo(() => {
    const ids = new Set(Array.from(forecastedItemIds || []).map(String));

    return (
      forecastApiData?.flatMap(
        (response) => {
          const responseRows = Array.isArray(response?.data)
            ? response.data
            : response?.data
              ? [response.data]
              : [];

          return responseRows.filter((item) =>
            ids.has(String(item?.quoteProductId))
          );
        }
      ) || []
    );
  }, [forecastApiData, forecastedItemIds]);

  const forecastSummaryRecords = useMemo(() => {
    return (forecastData?.data || []).filter((item) =>
      forecastedQuoteProductIds.includes(String(item?.quoteProductId))
    );
  }, [forecastData?.data, forecastedQuoteProductIds]);

  const forecastRows = useMemo(() => {
    return forecastApiRecords?.flatMap((record) => {
      const productForecasts = Array.isArray(record?.productForecasts) && record.productForecasts.length
        ? record.productForecasts
        : [{}];

      return productForecasts.map((forecastItem) => {
        const quoteProductId = forecastItem?.quoteProductId ?? record?.quoteProductId;
        const summary =
          forecastSummaryRecords?.find(
            (item) => String(item?.quoteProductId) === String(quoteProductId)
          ) || {};

        return {
          ...summary,
          ...record,
          quoteProductId,
          forecastRecordId: record?.id,
          forecastProductId:
            forecastItem?.forecastProductId || forecastItem?.id || 0,
          rowForecast: forecastItem,
          expectedDate:
            forecastItem?.expectedDate ||
            forecastItem?.forecastedDate ||
            "",
          expectedQuantity:
            forecastItem?.expectedQuantity ??
            summary?.quantity ??
            "",
          expectedAmount:
            forecastItem?.expectedAmount ??
            summary?.forecastedAmount ??
            0,
          histories: forecastItem?.histories || [],
          revisionNo: forecastItem?.revisionNo || 0,
        };
      });
    }) || [];
  }, [forecastApiRecords, forecastSummaryRecords]);

   const quotedAmount = useMemo(
    () =>
      forecastList.reduce(
        (sum, line) => sum + Number(line?.quotedAmount || 0),
        0
      ),
    [forecastList]
  );
  const forecastedAmount = useMemo(
    () =>
      forecastList.reduce(
        (sum, line) => sum + Number(line?.expectedAmount || 0),
        0
      ),
    [forecastList]
  );
  const balanceAmount = Math.max(quotedAmount - forecastedAmount, 0);

  const quotedQuantity = Number(currentForecast?.quantity || 0);
  const forecastedQuantity = useMemo(
    () =>
      forecastList.reduce(
        (sum, line) => sum + Number(line?.expectedQuantity || 0),
        0
    ),
    [forecastList]
  );

  useEffect(() => {
    if (!show) {
      setForecastList([]);
      setEditingLine(null);
      setEditValues({ expectedDate: "" });
      setOpenHistory({ show: false, currentItem: null });
      setChangeForecast(false);
      setErrorMessage("");
      setSelectedLineKeys([]);
      setBulkDate("");
      setCreateMsg(null);
      setShowSuccess(false);
      setErrorShow({ show: false, errorMessage: "" });
      return;
    }

    if (createMsg || showSuccess || errorShow?.show) {
      return;
    }

    setForecastList(forecastRows);
    setEditingLine(null);
    setEditValues({ expectedDate: "" });
    setOpenHistory({ show: false, currentItem: null });
    setChangeForecast(false);
    setErrorMessage("");
    setSelectedLineKeys([]);
    setBulkDate("");
  }, [show, forecastRows, createMsg, showSuccess, errorShow?.show]);

  const handleEditClick = (line) => {
    // A row date edit and a bulk date edit are mutually exclusive.
    setSelectedLineKeys([]);
    setBulkDate("");
    setEditingLine(line);
    setErrorMessage("");
    setEditValues({
      expectedDate: toInputDate(
        line?.expectedDate ||
        line?.forecastedDate ||
        line?.rowForecast?.expectedDate ||
        line?.productForecasts?.[0]?.expectedDate
      ),
    });
  };

  const handleEditDone = () => {
    setEditingLine(null);
    setEditValues({ expectedDate: "" });
  };

  const updateLineDate = (item, date) => {
    const apiDate = toApiDateString(date);
    const currentForecastItem = item?.rowForecast || item?.productForecasts?.[0] || {};
    const histories = normalizeHistories(
      currentForecastItem?.histories || item?.histories || []
    );
    const historyEntry = buildHistoryEntry(
      currentForecastItem?.forecastProductId || currentForecastItem?.id || 0,
      apiDate || currentForecastItem?.expectedDate || item?.expectedDate || item?.forecastedDate
    );
    historyEntry.previousDate = toApiDateString(
      currentForecastItem?.expectedDate || item?.expectedDate || item?.forecastedDate || ""
    );
    histories.push(historyEntry);

    const updatedForecastItem = {
      ...currentForecastItem,
      expectedDate: apiDate,
      expectedQuantity: Number(currentForecastItem?.expectedQuantity ?? item?.expectedQuantity ?? 0),
      expectedAmount: Number(currentForecastItem?.expectedAmount ?? item?.expectedAmount ?? 0),
      revisionNo: (Number(currentForecastItem?.revisionNo) || 0) + 1,
      histories,
    };

    return {
      ...item,
      rowForecast: updatedForecastItem,
      expectedDate: apiDate,
      expectedQuantity: updatedForecastItem.expectedQuantity,
      expectedAmount: updatedForecastItem.expectedAmount,
      histories,
      revisionNo: updatedForecastItem.revisionNo,
      productForecasts: [updatedForecastItem, ...(item?.productForecasts?.slice(1) || [])],
      isDirty: true,
    };
  };

  const buildHistoryEntry = (forecastProductId, forecastedDate) => ({
    id: 0,
    forecastProductId,
    previousDate: "",
    revisedDate: toApiDateString(forecastedDate),
    revisedOn: new Date(),
    createdOn: new Date(),
    createdBy: currentUserIdentityId,
    forecastedDate: toApiDateString(forecastedDate),
  });

  const normalizeHistories = (histories = []) =>
    histories.map((historyItem) => ({
      id: historyItem?.id || 0,
      forecastProductId: historyItem?.forecastProductId || 0,
      previousDate: historyItem?.previousDate || historyItem?.beforeDate || "",
      revisedDate:
        toApiDateString(
          historyItem?.revisedDate ||
          historyItem?.forecastedDate ||
          historyItem?.expectedDate ||
          ""
        ),
      revisedOn: historyItem?.revisedOn || historyItem?.createdOn || new Date(),
      createdOn: historyItem?.createdOn || new Date(),
      createdBy: historyItem?.createdBy || currentUserIdentityId,
      forecastedDate:
        toApiDateString(
          historyItem?.forecastedDate || historyItem?.expectedDate || ""
        ),
    }));

  const handleUpdateLine = () => {
    if (!editingLine) return;

    if (!editValues?.expectedDate) {
      setErrorMessage("Please select forecast date.");
      return;
    }

    const nextList = forecastList.map((item) => {
      const isSameLine =
        getForecastLineKey(item) === getForecastLineKey(editingLine);

      if (!isSameLine) return item;
      return updateLineDate(item, editValues.expectedDate);
    });

    setForecastList(nextList);
    if (openHistory?.show && openHistory?.currentItem) {
      const updatedItem = nextList.find(
        (item) =>
          String(item?.forecastRecordId || item?.id) ===
          String(editingLine?.forecastRecordId || editingLine?.id)
      );

      if (updatedItem) {
        setOpenHistory((prev) => ({
          ...prev,
          currentItem: updatedItem,
        }));
      }
    }
    setChangeForecast(true);
    handleEditDone();
  };

  const handleBulkDateUpdate = () => {
    if (!bulkDate) {
      setErrorMessage("Please select forecast date.");
      return;
    }

    const selectedKeys = new Set(selectedLineKeys);
    const nextList = forecastList.map((item, index) =>
      selectedKeys.has(getForecastLineKey(item, index))
        ? updateLineDate(item, bulkDate)
        : item
    );
    setForecastList(nextList);
    setChangeForecast(true);
    setErrorMessage("");
  };

  const toggleLineSelection = (line, index) => {
    // Selecting rows starts bulk-edit mode, so close any open row editor.
    handleEditDone();
    const lineKey = getForecastLineKey(line, index);
    setSelectedLineKeys((keys) =>
      keys.includes(lineKey) ? keys.filter((key) => key !== lineKey) : [...keys, lineKey]
    );
  };

  const forecastLineKeys = useMemo(
    () => forecastList.map((line, index) => getForecastLineKey(line, index)),
    [forecastList]
  );
  const isAllLinesSelected = forecastLineKeys.length > 0 &&
    forecastLineKeys.every((key) => selectedLineKeys.includes(key));
  const isPartiallySelected = selectedLineKeys.length > 0 && !isAllLinesSelected;

  useEffect(() => {
    if (selectAllLinesRef.current) {
      selectAllLinesRef.current.indeterminate = isPartiallySelected;
    }
    if (mobileSelectAllLinesRef.current) {
      mobileSelectAllLinesRef.current.indeterminate = isPartiallySelected;
    }
  }, [isPartiallySelected]);

  const toggleAllLineSelection = () => {
    // Selecting rows starts bulk-edit mode, so close any open row editor.
    handleEditDone();
    setSelectedLineKeys(isAllLinesSelected ? [] : forecastLineKeys);
  };

  const handleSaveForecast = async () => {
    const rowsToSave = forecastList.filter((item) => item?.isDirty);

    if (rowsToSave.length === 0) {
      return;
    }

    try {
      const dirtyRecordIds = new Set(
        rowsToSave.map((row) => String(row?.forecastRecordId || row?.id || row?.quoteProductId))
      );
      const recordsToSave = Array.from(dirtyRecordIds).map((recordId) =>
        forecastList.filter(
          (row) => String(row?.forecastRecordId || row?.id || row?.quoteProductId) === recordId
        )
      );

      await Promise.all(
        recordsToSave.map(
          (recordRows) =>
            new Promise((resolve, reject) => {
              const row = recordRows[0];
              const payload = {
                id: row?.forecastRecordId || row?.id || 0,
                quotationId: row?.quotationId || currentForecast?.quotationId || currentQuotation?.id,
                quoteProductId: row?.quoteProductId || currentForecast?.quoteProductId || currentForecast?.id,
                createdAt: row?.createdAt || new Date(),
                createdBy: currentUserIdentityId,
                forecastedAmount: Number(row?.expectedAmount || 0),
                forecastedQuantity: Number(row?.expectedQuantity || 0),
                productForecasts: recordRows.map((forecastRow) => ({
                  ...(forecastRow?.rowForecast || {}),
                  expectedDate: forecastRow?.expectedDate
                    ? toApiDateString(forecastRow.expectedDate)
                    : toApiDateString(forecastRow?.rowForecast?.expectedDate),
                  expectedQuantity: Number(forecastRow?.expectedQuantity || 0),
                  expectedAmount: Number(forecastRow?.expectedAmount || 0),
                  histories: normalizeHistories(
                    forecastRow?.histories || forecastRow?.rowForecast?.histories || []
                  ),
                  revisionNo: Number(forecastRow?.revisionNo || 0),
                })),
              };

              CreateEntry("forecast", payload, (response) => {
                if (response?.status === 200 || response?.status === 204) {
                  resolve(response);
                } else {
                  reject(response);
                }
              }, "Forecast updated successfully", "Failed to update forecast");
            })
        )
      );

      setChangeForecast(false);
      setCreateMsg({
        successData: "Successful",
        message: "Forecast updated successfully",
        status: 200,
      });
      loadForecastApiData();
    } catch (error) {
      setCreateMsg({
        successData: error?.message || error || "Failed to update forecast",
        message: "Failed to update forecast",
        status: 400,
      });
    }
  };

  const handleCloseHistory = () => {
    setOpenHistory({ show: false, currentItem: null });
  };

  useEffect(() => {
    if (!createMsg) return;

    if (String(createMsg?.message).includes("Failed")) {
      setErrorShow({
        show: true,
        errorMessage: createMsg?.successData || createMsg?.message || "Failed to update forecast",
      });
    } else if (createMsg?.status === 200 || createMsg?.status === 204) {
      setShowSuccess(true);
      if (currentQuotation?.id && setQuotationData) {
        GetList("forecast", { quoteId: currentQuotation?.id }, setQuotationData);
      }
    }
  }, [createMsg, currentQuotation?.id, onHide, setQuotationData]);

  const SuccessDoneClick = () => {
    setShowSuccess(false);
    setCreateMsg(null);
    onHide()
  };

  const ErrorDoneClick = () => {
    setErrorShow({ show: false, errorMessage: "" });
  };
  return (
    <>
      <Modal
        size="sm"
        show={show}
        onHide={onHide}
        className="forecastRightSideModalPopup drawer right-align"
      >
        <Modal.Header>
          <h5 className="modal-title">
            {/* {currentForecast?.productGroupName} - {currentForecast?.productName} */}
            Forecasted Items ({forecastList.length})
          </h5>
          <i className="fas fa-times" onClick={onHide}></i>
        </Modal.Header>
        <Modal.Body>
          <div className="row modal-row mt-2">
            <div className="col-xxl-4 col-xl-4 col-lg-4 col-md-4 col-sm-4 col-xs-4">
              <div className="quoted-col">
                <p>Quoted ({currencyDetail?.shortCode})</p>
                <h4>
                  {FormatValueDecimal(
                    CurrencyConvertion(
                      quotedAmount,
                      currentQuotation?.row?.conversionRate,
                      currencyDetail
                    )
                  )}
                </h4>
              </div>
            </div>
            <div className="col-xxl-4 col-xl-4 col-lg-4 col-md-4 col-sm-4 col-xs-4">
              <div className="forecasted-col">
                <p>Forecasted ({currencyDetail?.shortCode})</p>
                <h4>
                  {FormatValueDecimal(
                    CurrencyConvertion(
                      forecastedAmount,
                      currentQuotation?.row?.conversionRate,
                      currencyDetail
                    )
                  )}
                </h4>
              </div>
            </div>
            <div className="col-xxl-4 col-xl-4 col-lg-4 col-md-4 col-sm-4 col-xs-4">
              <div className="balance-col">
                <p>Balance ({currencyDetail?.shortCode})</p>
                <h4>
                  {FormatValueDecimal(
                    CurrencyConvertion(
                      balanceAmount,
                      currentQuotation?.row?.conversionRate,
                      currencyDetail
                    )
                  )}
                </h4>
              </div>
            </div>
          </div>

          <div className="header mt-2">
            <h6>Forecast Details</h6>
          </div>

          {editingLine && (
            <div
              className="col-xxl-12 col-xl-12 col-lg-12 col-md-12 col-sm-12 col-xs-12">
                <div className="mt-2">
                  <label>Date</label>
                  <DatePicker
                    selected={editValues?.expectedDate ? new Date(editValues.expectedDate) : null}
                    onChange={(date) =>
                      setEditValues((prev) => ({
                        ...prev,
                        expectedDate: date ? toInputDate(date) : "",
                      }))
                    }
                    customInput={<ExampleCustomInput />}
                    dayClassName={() => "example-datepicker-day-class"}
                    popperClassName="example-datepicker-class"
                    todayButton="TODAY"
                    popperPlacement="bottom-start"
                    popperProps={{ strategy: "fixed" }}
                    dateFormat="dd/MM/yyyy"
                    minDate={new Date()}
                  />
                </div>
                <div className="d-flex justify-content-between gap-2 align-items-start">
                <div className="mt-2">
                  <label>Quantity</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editingLine?.expectedQuantity ?? quotedQuantity ?? ""}
                    disabled
                  />
                </div>
                <div className="mt-2 ml-2">
                  <label>Forecast Amount</label>
                  <div className="d-flex">
                  <input
                    type="text"
                    className="form-control"
                    value={
                      editingLine?.forecastedAmount !== undefined
                        ? FormatValueDecimal(
                            CurrencyConvertion(
                              editingLine?.forecastedAmount,
                              currentQuotation?.row?.conversionRate,
                              currencyDetail
                            )
                          )
                        : ""
                    }
                    disabled
                  />
                   {errorMessage && <p className="form-error mt-2 mb-0">{errorMessage}</p>}
                <button
                  type="button"
                  className="btn add-btn"
                  onClick={handleUpdateLine}
                >
                  Update
                </button>
              </div>
                </div>
                </div>
             
            </div>
          )}

          {selectedLineKeys.length > 0 && (
            <div className="mt-3 d-flex justify-content-between align-items-end gap-2">
              <div>
                <DatePicker
                  selected={bulkDate ? new Date(bulkDate) : null}
                  onChange={(date) => {
                    setBulkDate(date ? toInputDate(date) : "");
                    setErrorMessage("");
                  }}
                  customInput={<ExampleCustomInput />}
                  dayClassName={() => "example-datepicker-day-class"}
                  popperClassName="example-datepicker-class"
                  todayButton="TODAY"
                  popperPlacement="bottom-start"
                  popperProps={{ strategy: "fixed" }}
                  dateFormat="dd/MM/yyyy"
                  minDate={new Date()}
                />
              </div>
              <button
                type="button"
                className="btn add-btn"
                onClick={handleBulkDateUpdate}
                disabled={!bulkDate}
              >
                Update
              </button>
              {errorMessage && <p className="form-error mb-0">{errorMessage}</p>}
            </div>
          )}

          <label className="forecast-detail-mobile-select-all d-block d-md-none">
            <input
              ref={mobileSelectAllLinesRef}
              type="checkbox"
              checked={isAllLinesSelected}
              disabled={forecastLineKeys.length === 0}
              onChange={toggleAllLineSelection}
              aria-label="Select all forecast rows"
              className="forecast-detail-checkbox forecast-detail-select-all"
            />
            <span>Select all</span>
          </label>

          <div className="table-responsive mt-3 forecast-detail-table-wrap">
            <table className="table m-0 forecast-table">
              <thead>
                <tr>
                  <th className="forecast-detail-select-header">
                    <input
                      ref={selectAllLinesRef}
                      type="checkbox"
                      checked={isAllLinesSelected}
                      disabled={forecastLineKeys.length === 0}
                      onChange={toggleAllLineSelection}
                      aria-label="Select all forecast rows"
                      className="forecast-detail-checkbox forecast-detail-select-all"
                    />
                  </th>
                  <th>Product</th>
                  <th>Model</th>
                  <th>Quoted Qty</th>
                  <th>Forecasted Value</th>
                  <th>Forecasted Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {forecastList.map((line, index) => (
                <tr key={getForecastLineKey(line, index)}>
                  <td className="text-center forecast-detail-select-cell">
                    <input
                      type="checkbox"
                      checked={selectedLineKeys.includes(getForecastLineKey(line, index))}
                      onChange={() => toggleLineSelection(line, index)}
                      aria-label={`Select ${line?.productName || "forecast"} for date update`}
                      className="forecast-detail-checkbox"
                    />
                  </td>
                  <td className="forecast-product-cell" data-label="Product">
                    {line?.productGroupName || currentForecast?.productGroupName || "-"}
                  </td>
                  <td data-label="Model">
                    {line?.productName || currentForecast?.productName || "-"}
                  </td>
                  <td className="text-right" data-label="Quoted Qty">
                    {line?.expectedQuantity ?? quotedQuantity ?? "-"}
                  </td>
                  <td className="text-right" data-label="Forecasted Value">
                    {FormatValueDecimal(
                      CurrencyConvertion(
                        line?.expectedAmount,
                        currentQuotation?.row?.conversionRate,
                        currencyDetail
                      )
                    )}
                  </td>
                  <td data-label="Forecasted Date">
                    {line?.expectedDate
                      ? moment(new Date(line.expectedDate)).format("DD-MMM-YYYY")
                      : "-"}
                  </td>
                  <td className="forecast-actions-cell">
                    <span>
                      <i className="far fa-edit mr-2" title="Edit" onClick={() => handleEditClick(line)} />
                    </span>
                    <span>
                      <i
                        className="fa fa-history mr-2"
                        title="History"
                        onClick={() => setOpenHistory({ show: true, currentItem: line || null })}
                      />
                    </span>
                  </td>
                </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <div className="d-flex justify-content-end">
            <div className="text-align">
              <button
                type="button"
                className="btn cancel-btn mr-3"
                onClick={onHide}
              >
                Cancel
              </button>
            </div>
            <div className="text-align">
              <button
                type="button"
                className="btn save-btn"
                onClick={handleSaveForecast}
                disabled={!changeForecast && forecastList.length > 0}
              >
                Save
              </button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal
        size="sm"
        show={openHistory?.show}
        onHide={handleCloseHistory}
        className="historyRightSideModalPopup drawer right-align"
      >
        <Modal.Header>
          <div className="d-flex align-items-center">
            <i className="fas fa-chevron-left" onClick={handleCloseHistory}></i>
            <h5 className="modal-title ml-2">Forecast History</h5>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="row forecast-history-table-row mt-3">
            <div className="col-12">
              <div className="table-responsive">
                <table className="table table-borderless m-0">
                  <thead>
                      <tr>
                      <th>Revision</th>
                      <th>Forecast Date</th>
                      <th>Revised Date</th>
                      <th>Revised On</th>
                      <th>Revised By</th>
                      </tr>
                  </thead>
                 <tbody>
                    {buildHistoryTimelineRows(
                        openHistory?.currentItem?.histories || []
                    ).map((historyItem, historyIndex) => {

                        const filterUser = userData?.find(
                            (userItem) =>
                                userItem?.identityId === historyItem?.createdBy
                        );

                        const revisionNo = Number(
                            historyItem?.revisionNo ?? historyIndex
                        );

                        const isInitialRevision = revisionNo === 0;

                        return (
                          <tr key={historyIndex}>
                            <td className="text-nowrap history-rev-cell">
                              Rev {String(revisionNo).padStart(2, "0")}
                            </td>
                            <td className="text-nowrap" data-label="Forecast Date">
                              {formatHistoryDate(historyItem?.forecastedDate)}
                            </td>
                            <td className="text-nowrap" data-label="Revised Date">
                              {isInitialRevision ? "-" : formatHistoryDate(historyItem?.revisedDate)}
                            </td>
                            <td className="text-nowrap" data-label="Revised On">
                              {isInitialRevision ? "-" : formatHistoryDate(historyItem?.revisedOn, true)}
                            </td>
                            <td className="text-nowrap" data-label="Revised By">
                              {filterUser?.firstName || ""} {filterUser?.lastName || ""}
                            </td>
                          </tr>
                        );
                    })}
                </tbody>
                </table>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <SaveSuccessModel
        createMsg={createMsg}
        setCreateMsg={setCreateMsg}
        setShow={setShowSuccess}
        show={showSuccess}
        SuccessDoneClick={SuccessDoneClick}
      />
      <ErrorMessage
        Show={errorShow?.show}
        errorMessage={errorShow?.errorMessage}
        ErrorDoneClick={ErrorDoneClick}
      />
    </>
  );
};

export default ForecastViewModal;
